import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { Dispatch, SetStateAction } from 'react';
import type { Selection } from './orgNavTypes';

/**
 * Manages the state of the OrgNav component, uses URL search params to store the state.
 * Purpose is to allow usage of links to navigate to different companies, resellers, locations, etc.
 */

export type SelectionState = {
    type: Selection | null;
    id: string | number | null;
};

export const useOrgNavState = () => {
    const [searchParams, setSearchParams] = useSearchParams(new URLSearchParams());

    const state = useMemo(() => {
        const { type, id } = Object.fromEntries(searchParams);

        return {
            type: type || null,
            id: id ? (isNaN(Number(id)) ? id : parseInt(id)) : null,
        } as SelectionState;
    }, [searchParams]);

    const set: Dispatch<SetStateAction<SelectionState>> = (action) => {
        const next = typeof action === 'function' ? action(state) : action;

        setSearchParams(sanitizeParams(next));
    };

    return [state, set] as const;
};

function sanitizeParams(data: Record<string, string | number | null | string[]>) {
    const params = new URLSearchParams();

    Object.entries(data).forEach(([key, value]) => {
        if (value === null) return;
        const val = Array.isArray(value) ? value.join(',') : value.toString();
        if (val) params.set(key, val);
    });

    return params;
}
